import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { relativeDate } from '@/lib/taskRules'
import { EmptyNote, RowSkeleton } from './shared'
import type { AuditEntry } from '@/api/types'

const PAGE_SIZE = 8

/**
 * Where the rows come from.
 *
 * - `audit` — `GET /audit`, permission `audit:read`, i.e. qa and admin only.
 *   Rows deep-link into `/audit`, which those two roles can open.
 * - `dashboard` — `GET /dashboard/activity`, permission `dashboard:read`, i.e.
 *   maintainer and admin. A fixed action allow-list, no export, no arbitrary
 *   filters, which is what makes it narrower than `audit:read` rather than a
 *   rename of it. Rows are NOT links: a maintainer cannot open `/audit`, and a
 *   link to a 403 is worse than no link.
 */
type FeedSource = 'audit' | 'dashboard'

/**
 * A short read-only slice of `GET /audit`.
 *
 * Rows are deep links now that `/audit` keeps its filters in `useSearchParams`:
 * `/audit?action=…&resource_id=…` lands on exactly the row that was clicked. Two
 * params, not one — action alone would open a whole category, and the operator
 * clicked a specific event. Entries with no `resource_id` (a login, a settings
 * write) link on action alone.
 */
export function AuditFeed({
  action,
  emptyCopy = 'Nothing yet.',
  actorLabel = true,
  source = 'audit',
}: {
  /** Exact `audit_log.action` value, or omitted for the newest of everything. */
  action?: string
  emptyCopy?: string
  actorLabel?: boolean
  source?: FeedSource
}) {
  const filters = action ? { action } : {}
  const { data, isLoading } = useQuery<{ items: AuditEntry[] }>({
    // Same key shape `/audit` uses — `['audit', filters, page]` — with the
    // source in the key so the two endpoints can't share a cache entry.
    queryKey: ['audit', source, filters, 1],
    queryFn: () =>
      source === 'dashboard'
        ? apiClient.getActivityFeed({ limit: PAGE_SIZE, action })
        : apiClient.fetchAudit(filters, 1, PAGE_SIZE),
  })

  if (isLoading) return <RowSkeleton rows={3} />
  const items = data?.items ?? []
  if (items.length === 0) return <EmptyNote>{emptyCopy}</EmptyNote>

  return (
    <Card className="divide-y">
      {items.map((e) => (
        <AuditRow key={e.id} entry={e} actorLabel={actorLabel} linked={source === 'audit'} />
      ))}
    </Card>
  )
}

function AuditRow({
  entry,
  actorLabel,
  linked,
}: {
  entry: AuditEntry
  actorLabel: boolean
  linked: boolean
}) {
  const query = new URLSearchParams({ action: entry.action })
  if (entry.resource_id) query.set('resource_id', entry.resource_id)

  const body = (
    <>
      <div className="min-w-0">
        <p className="truncate font-mono text-sm">{entry.action}</p>
        <p className="truncate text-xs text-muted-foreground">
          {entry.resource_type ? `${entry.resource_type} ` : ''}
          {entry.resource_id && <span className="font-mono">{entry.resource_id}</span>}
          {actorLabel && entry.actor_role ? ` · by ${entry.actor_role}` : ''}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {/* `ok` is the default outcome — hue only for the ones a human should
            look at (`denied`, `failed`); anything else non-default stays grey. */}
        {entry.outcome !== 'ok' && (
          <Badge
            variant={
              entry.outcome === 'denied' || entry.outcome === 'failed' ? 'warning' : 'muted'
            }
          >
            {entry.outcome}
          </Badge>
        )}
        <span className="font-mono text-xs text-muted-foreground">{relativeDate(entry.ts)}</span>
      </div>
    </>
  )

  const row = 'flex items-start justify-between gap-3 p-3'
  if (!linked) return <div className={row}>{body}</div>
  return (
    <Link to={`/audit?${query}`} className={`${row} transition-colors hover:bg-muted/50`}>
      {body}
    </Link>
  )
}
